import type { Audience } from "@/client/core/Core.api";
import type { AudienceState } from "@/client/core/services/audience";
import {
    audienceStateSignal,
    setForcedAudience,
} from "@/client/core/services/feature-flags.signal";
import { createEl } from "@/client/utils/createEl";
import * as styles from "./audience-tag.css";

// Highest tier wins the label. "soundcheck" members read as Soundcheck, the
// name of the artist program they belong to.
const kAudienceRank: readonly Audience[] = ["rnd", "internal", "soundcheck", "partner"];

const kAudienceDisplay: Record<Audience, string> = {
    rnd: "R&D",
    internal: "Internal",
    soundcheck: "Soundcheck",
    partner: "Partner",
};

// Click-to-cycle preview order for privileged users; undefined = actual.
const kPreviewCycle: readonly (string | undefined)[] = [
    undefined,
    "internal",
    "soundcheck",
    "partner",
    "none",
];

const getLabel = (state: Readonly<AudienceState>): string => {
    const top = kAudienceRank.find((audience) => state.audiences.has(audience));
    return top !== undefined ? kAudienceDisplay[top] : "No access";
};

/**
 * The access-tier label beside the Backstage headline ("Backstage ·
 * Internal"). Every member sees their tier; R&D users (and dev builds) can
 * click it to cycle an audience preview — this replaces the menu's old
 * "View as audience" dropdown. Previews persist like `?forceAudience=`
 * (sessionStorage), so a preview set here follows into app pages.
 */
export const createAudienceTag = (): HTMLElement => {
    const labelEl = createEl("span", styles.label, "");
    const tagEl = createEl("button", styles.tag, [labelEl], (el) => {
        el.type = "button";
    });

    let canForce = false;
    let currentForced: string | undefined;

    tagEl.addEventListener("click", () => {
        if (!canForce) {
            return;
        }
        const index = kPreviewCycle.indexOf(currentForced);
        const next = kPreviewCycle[(index + 1) % kPreviewCycle.length];
        setForcedAudience(next);
    });

    audienceStateSignal.listenAndInit((state) => {
        if (!state.isReady) {
            tagEl.classList.add(styles.tagHidden);
            return;
        }
        tagEl.classList.remove(styles.tagHidden);
        canForce = import.meta.env.DEV || state.resolvedAudiences.has("rnd");
        currentForced = state.forcedAudience;
        labelEl.textContent = getLabel(state);
        tagEl.classList.toggle(styles.tagInteractive, canForce);
        tagEl.classList.toggle(styles.tagPreviewing, state.forcedAudience !== undefined);
        tagEl.disabled = !canForce;
        tagEl.title = canForce
            ? state.forcedAudience !== undefined
                ? "Previewing another audience — click to keep cycling (back to your actual access)"
                : "Your access tier — click to preview other audiences"
            : "Your access tier";
    });

    return tagEl;
};
